import {
  UsePanelStateOptions,
  usePanelState,
} from '@/app/navigation/usePanelState';
import { RowSelectionState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

/**
 * Hook for managing row selection state in panel-based tables.
 * Syncs selection with panel state (URL params) to persist across navigation.
 *
 * Selection is stored as a comma-separated list of row IDs in the URL.
 * Example: A0.sel=id1,id2,id3
 */
export function usePanelSelectionState(options?: UsePanelStateOptions) {
  const [state, setState] = usePanelState(options);

  // Parse selection from URL string to TanStack Table format
  const rowSelection = useMemo<RowSelectionState>(() => {
    const selString = state.sel;
    if (!selString || typeof selString !== 'string') {
      return {};
    }
    const ids = selString.split(',').filter(Boolean);
    return Object.fromEntries(ids.map((id) => [id, true]));
  }, [state.sel]);

  // Update selection in URL
  const setRowSelection = useCallback(
    (
      updater:
        | RowSelectionState
        | ((prev: RowSelectionState) => RowSelectionState),
    ) => {
      const newSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater;

      const selectedIds = Object.keys(newSelection).filter(
        (id) => newSelection[id],
      );

      setState((prev) => {
        if (selectedIds.length === 0) {
          // Clear selection from state
          const { sel, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          sel: selectedIds.join(','),
        };
      });
    },
    [rowSelection, setState],
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setState((prev) => {
      const { sel, ...rest } = prev;
      return rest;
    });
  }, [setState]);

  return {
    rowSelection,
    setRowSelection,
    clearSelection,
  };
}
